// https://reacttraining.com/reach-ui/dialog/
import React, { useContext } from "react"
import PropTypes from "prop-types"
import { graphql, notGatsby } from "gatsby"
import { DialogContent } from "@reach/dialog"
import styled, { ThemeContext } from "styled-components"
import {
  background,
  border,
  color,
  compose,
  flexbox,
  layout,
  position,
  shadow,
  space,
} from "styled-system"

import { hexToRGBA } from "../../lib/color-helpers"
import ThemeSwitcher from "../utils/ThemeSwitcher"

import Box from "./Box"
import Button from "./Button"
import Close from "../icons/Close"
import Flex from "./Flex"

const StyledDialogContent = styled(DialogContent).withConfig({
  displayName: "StyledDialogContent",
})`
  ${compose(
    background,
    border,
    color,
    flexbox,
    layout,
    position,
    shadow,
    space
  )}

  outline: none;

  //temporary fixed for customHTML popups. check if it does not interfere with other popups popup type
  .close-btn-container ~ div div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`
/**
 * Modal - used for consistent styling of modals throughout site
 * @param {JSX.Element} children - Modal content
 * @param {boolean} isOpen - Controls if the modal is open
 * @param {function} close - Function to call when closing the modal
 * @param {string} ariaLabel - Accessibility label for the modal
 * @param {boolean} showCloseBtn - Set to false to hide the close button
 * @param {object} overlayStyle - Style overrides for the overlay
 * @param {object} contentStyle - Style overrides for the content
 * @param {string} theme - Theme key to use
 * @param {string} bg - Background key for the overlay
 * @param {number} overlayOpacity - Number between 0 and 1 controlling overlay opacity
 * @param {object} size - Size options for the modal
 * @param {object} shadow - Shadow options for the modal
 * @param {object} image - Image prop
 */
const Modal = ({
  children,
  isOpen,
  close,
  ariaLabel,
  showCloseBtn = true,
  overlayStyle,
  contentStyle,
  theme,
  bg,
  overlayOpacity,
  size,
  shadow,
  image,
  ...rest
}) => {
  const currentTheme = useContext(ThemeContext)
  const overlayHexColor =
    currentTheme?.themes[theme]?.colors?.background[bg] || "#000000"

  const overlayColor =
    overlayOpacity === 0
      ? "transparent"
      : hexToRGBA(overlayHexColor, overlayOpacity * 100)
  // if (!notGatsby) {
  //   // making client site page non-scrollable when model is open
  //   const bodyWrapper = document.querySelector("#gatsby-focus-wrapper")
  //   bodyWrapper.style.height = isOpen ? "100vh" : "auto"
  //   bodyWrapper.style.overflow = isOpen ? "hidden" : "auto"
  // }

  return (
    <ThemeSwitcher theme={theme}>
      <Box
        backgroundColor={overlayColor}
        zIndex={100}
        justifyContent="center"
        display="flex"
        isOpen={isOpen}
        onDismiss={close}
        onClick={close}
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        {...overlayStyle}
      >
        <StyledDialogContent
          className="dialog-content"
          aria-label={ariaLabel}
          zIndex={1000}
          margin={0}
          position="absolute"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          background="transparent"
          width={
            size?.sizeSelections === "custom"
              ? `${size?.customSize}px`
              : size?.sizeSelections === "xs"
              ? 600
              : size?.sizeSelections === "sm"
              ? 50 //800px
              : size?.sizeSelections === "md"
              ? 63 //"1008px"
              : size?.sizeSelections === "lg"
              ? 81 //"1296px"
              : "100%"
          }
          marginTop="10vh"
          maxWidth={{ _: "85%" }}
          {...contentStyle}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="stretch"
            alignItems="stretch"
            boxShadow={
              shadow?.sizeSelections === "none"
                ? "none"
                : `${shadow?.sizeSelections}`
            }
          >
            <Flex
              width="100%"
              justifyContent="flex-end"
              className="close-btn-container"
            >
              {showCloseBtn && (
                <Button
                  aria-label="Close popup modal button"
                  variant="filled"
                  bg="transparent"
                  onClick={close}
                  mt={-6}
                  mb={3}
                  style={{ paddingLeft: "0px", paddingRight: "0px" }}
                >
                  <Close />
                </Button>
              )}
            </Flex>
            {children}
          </Box>
        </StyledDialogContent>
      </Box>
    </ThemeSwitcher>
  )
}

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onDismiss: PropTypes.func,
  ariaLabel: PropTypes.string.isRequired,
  image: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
  }),
  close: PropTypes.func,
  showCloseBtn: PropTypes.bool,
  overlayStyle: PropTypes.object,
  contentStyle: PropTypes.object,
  theme: PropTypes.string,
  bg: PropTypes.string,
  overlayOpacity: PropTypes.number,
  size: PropTypes.object,
  shadow: PropTypes.object,
}

export default Modal

export const query = graphql`
  fragment Modal on STRAPI__COMPONENT_ATOMS_MODAL {
    id
    size {
      customSize
      sizeSelections
    }
    shadow {
      customSize
      sizeSelections
    }
  }
`
